import { composeWithDevTools } from '@redux-devtools/extension';
import { type CallhomeSite, type SiteId } from '@solinkcorp/api-types';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { ALL_LOCATIONS, type HistogramRange } from '@solinkcorp/vd-common';
import { type Action } from 'actions/index';
import { StorageAPI } from 'clients';
import { VIDEOWALL_SOURCE } from 'components/VideoWallPage/constants';
import { createStoreWithFirebase, firestoreReducer } from 'configs/firebase.config';
import { type Draft } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import * as reactRedux from 'react-redux';
import reducers from 'reducers';
import { initialState as initialFilterState } from 'reducers/filterReducer';
import { applyMiddleware, combineReducers, type Store } from 'redux';
import { createLogger } from 'redux-logger';
import { thunk, type ThunkAction, type ThunkDispatch } from 'redux-thunk';
import { PERSISTENT_PREFIX } from 'src/core/utils/StorageAPI';
import { validateHistogramRange } from 'utils/EventsFetchUtil';
import { getAnyPresetDeviceId, getQueryProps } from 'utils/RouteUtil';
import { KIOSK_LOCATION, LOCAL_FILTERS } from 'vd-constants';
import { loadStores, persistStores } from './persistentStores';

// List of actions to not log.
const muteList: Action['type'][] = ['FILTER_SET_DATE'];

const middlewares = [
  thunk,
  createLogger({
    predicate: (_getState, action) => !muteList.includes(action.type),
  }) as any,
];

const rootReducer = combineReducers({
  ...reducers,
  firestore: firestoreReducer,
});

export type RootState = ReturnType<typeof rootReducer>;
export type VDStore = Store<RootState, Action>;

export function createStore() {
  // Any top-level state trees in ths list will be automatically persisted to local storage.
  const PERSISTENT_STORES: (keyof RootState)[] = ['filter', 'fieldStats'];

  const loadedInitialState = merge(loadOldState(), loadStores<RootState>(PERSISTENT_STORES));
  loadStateFromURL(loadedInitialState);
  const composeEnhancers = composeWithDevTools({ trace: true, traceLimit: 25 });

  // Create store with reducers and initial state
  const store: Store<RootState, Action> = createStoreWithFirebase(
    rootReducer,
    loadedInitialState,
    composeEnhancers(applyMiddleware(...middlewares)),
  );

  persistStores(store, PERSISTENT_STORES);

  // For debugging.
  if (typeof window !== 'undefined') {
    (window as any).reduxStore = store;
  }

  return store;
}

let store: VDStore | undefined;

export function getReduxStore(): VDStore {
  // Lazy initialize store.
  if (store === undefined) {
    store = createStore();
  }
  return store;
}

/**
 * A hook to access the redux store's state. This hook takes a selector function
 * as an argument. The selector is called with the store state.
 *
 * This hook takes an optional equality comparison function as the second parameter
 * that allows you to customize the way the selected state is compared to determine
 * whether the component needs to be re-rendered.
 *
 * If you do not want to have to specify the root state type for whenever you use
 * this hook with an inline selector you can use the `TypedUseSelectorHook` interface
 * to create a version of this hook that is properly typed for your root state.
 *
 * @param selector the selector function
 * @param equalityFn the function that will be used to determine equality
 *
 * @returns the selected state
 *
 * @example
 *
 * import React from 'react'
 * import { useSelector } from 'stores/store'
 *
 * export const CounterComponent = () => {
 *   const counter = useSelector(state => state.counter)
 *   return <div>{counter}</div>
 * }
 */
export function useSelector<TSelected>(
  selector: (state: RootState) => TSelected,
  equalityFn?: (left: TSelected, right: TSelected) => boolean,
) {
  // This is just a thin wrapper around `reactRedux.useSelector` which
  // automatically sets the correct state for the selector function.
  // This just makes it a little easier to write type-safe `useSelector`s.
  // It also gives you better intellisense in js files.
  return reactRedux.useSelector(selector, equalityFn);
}

export type DispatchType = ThunkDispatch<RootState, any, Action>;

type VideoDiscoveryThunkAction<T> = ThunkAction<T, RootState, any, Action>;
export type { VideoDiscoveryThunkAction as ThunkAction };

export function useDispatch() {
  return reactRedux.useDispatch() as DispatchType;
}

export function useStore(): VDStore {
  return reactRedux.useStore<RootState, Action>();
}

export const connect: reactRedux.Connect<RootState> = reactRedux.connect;

/**
 * Loads state from legacy local storage.
 */
function loadOldState() {
  const result: Partial<RootState> = {};

  const storedFilterState = StorageAPI.getLocally(LOCAL_FILTERS) as {
    date?: string;
    location?: CallhomeSite;
    range?: HistogramRange;
    timezoneId?: string;
    locationTags?: string[];
  };

  if (storedFilterState) {
    const { date, location, locationTags, range, timezoneId } = storedFilterState;
    try {
      result.filter = {
        date: date ? new Date(date).valueOf() : Date.now(),
        locationIds: [(location && location.id) ?? (ALL_LOCATIONS as SiteId)],
        locationTags: locationTags ?? [],
        range: range ?? 'Day',
        timezoneId: timezoneId ?? getLocalTimeZone(),
        cameraGroupId: null,
        selectedCameraIds: [],
        selectedLocationId: null,
        cameraSelectMode: null,
        spotCheckMode: 'scheduled',
        spotCheckTitle: '',
      };
    } catch (_error) {
      // Ignore
    }
    StorageAPI.removeLocally(LOCAL_FILTERS);
  }

  return result;
}

/**
 * Loads state overrides from the URL.
 */
function loadStateFromURL(state: Draft<RootState>) {
  if (!state.filter) {
    state.filter = cloneDeep(initialFilterState);
  }

  const queryProps = getQueryProps();
  const { date, range, locationId, siteIds, source } = queryProps;

  if (date) {
    state.filter.date = date.valueOf();
  }

  let locationIdsFilter: readonly SiteId[] | undefined;
  const presetDeviceId = getAnyPresetDeviceId();
  const kioskLocationId = StorageAPI.getLocally<SiteId>(PERSISTENT_PREFIX + KIOSK_LOCATION);

  const shouldInitializeLocationIdFilter = source !== VIDEOWALL_SOURCE;

  if (shouldInitializeLocationIdFilter) {
    if (siteIds) {
      locationIdsFilter = siteIds;
    } else if (locationId) {
      locationIdsFilter = [locationId];
    } else if (presetDeviceId) {
      locationIdsFilter = [presetDeviceId];
    } else if (kioskLocationId) {
      locationIdsFilter = [kioskLocationId];
    }
  }

  if (locationIdsFilter) {
    state.filter.locationIds = locationIdsFilter.slice();
  }

  const validatedRange = validateHistogramRange(range);
  if (validatedRange) {
    state.filter.range = validatedRange;
  }
}
